import { makeAutoObservable } from 'mobx';
import { env, type AppEnv } from '@/lib/env';
import { EchoClient } from '@/realtime/echo-client';
import {
  NEW_ORDER_EVENT,
  ORDER_STATUS_EVENT,
  ordersChannel,
  type ChannelLike,
  type ConnectionCallback,
  type EventCallback,
  type RealtimeClientLike,
} from '@/realtime/contract';
import { extractNewOrderHint, extractOrderId, type NewOrderHint } from '@/realtime/events';
import type { ExternalChangeHint } from './OrderStore';
import type { Order } from '@/types/orders';

/**
 * RealtimeStore (V1-05) — the console's connection to the EXISTING
 * Reverb/Pusher host (06-realtime.md). HTTP stays authoritative: events are
 * invalidation hints handed to OrderStore's coalesced entrypoint.
 *
 * Lifecycle (lead wiring):
 *  - `connect(organizationId)` AFTER auth + organization selection;
 *  - `connect(newOrgId)` on organization switch (tears down the previous
 *    subscription first); `disconnect()` on logout;
 *  - `env.reverb === null` → PERMANENT polling mode (no key is ever
 *    hardcoded); the connection badge shows Polling.
 *
 * States: connecting → live; socket loss → reconnecting; >10 s without
 * recovery → polling every 30 s (jittered exponential backoff after errors);
 * reconnect → ONE immediate full refresh, then polling stops. `offline`
 * mirrors the browser connectivity signal — polling never pretends to cover
 * API/network loss (online-only console).
 *
 * Chime: after every successful active-board reconcile (event, polling, and
 * reconnect paths all converge there), orders not yet seen by THIS TAB with
 * source `web` trigger ONE chime per batch while the day is open; each order
 * id chimes at most once per tab session, so reconnect/polling overlap can
 * never repeat it. Audio autoplay denial degrades to the notifier's toast.
 *
 * Security (09-quality §6): this module NEVER logs the websocket key, URLs,
 * or event/order/customer payloads.
 */

export type RealtimeState = 'connecting' | 'live' | 'reconnecting' | 'polling' | 'offline';

/** Sound/toast seam — see src/features/pos/queue/chime.ts createOrderNotifier(). */
export interface RealtimeNotifier {
  /** Plays the bundled chime; resolves false when playback is blocked/fails. */
  playChime(): Promise<boolean>;
  /** Always show the visual new-order notification when this hook is available. */
  onNewOrders?(orders: readonly Order[]): void;
  /** Realtime fast path; carries no customer or money fields. */
  onNewOrderHint?(order: NewOrderHint): void;
  /** Legacy visual fallback for notifiers that only report blocked audio. */
  onChimeBlocked(orders: readonly Order[]): void;
}

/** The OrderStore surface this store drives (structural, test-friendly). */
export interface RealtimeOrdersLike {
  notifyExternalChange(hint: ExternalChangeHint): void;
  reconcileActive(): Promise<boolean>;
  onActiveReconciled(listener: (orders: Order[]) => void): () => void;
}

/**
 * The TableStore surface this store drives. Optional: the console runs
 * unchanged for an organization with no floor, and existing tests construct
 * RealtimeStore without it.
 */
export interface RealtimeTablesLike {
  notifyExternalChange(): void;
  reconcileFloor(): Promise<boolean>;
}

export interface RealtimeStoreOptions {
  orders: RealtimeOrdersLike;
  /**
   * A tab IS an order, so the same `orders.{organizationId}` channel and the
   * same two events already describe every change a table can make. Wiring the
   * floor here rather than opening a second subscription keeps the ID-only
   * privacy property of the public channel intact.
   */
  tables?: RealtimeTablesLike | null;
  /** Defaults to env.reverb; null → permanent polling mode. */
  reverb?: AppEnv['reverb'];
  /** Client factory (default: EchoClient). Tests inject FakeReverbClient. */
  createClient?: (config: NonNullable<AppEnv['reverb']>) => RealtimeClientLike;
  /** Chime/toast seam; omitted → no sound (documented lead wiring). */
  notifier?: RealtimeNotifier | null;
  /**
   * Day-open gate for the chime (06-realtime: chime while day open). Default
   * `() => true`; the lead wires SessionStore's open-session flag here.
   */
  isDayOpen?: () => boolean;
  /** How long a socket loss may last before polling starts (default 10 s). */
  disconnectGraceMs?: number;
  /** Polling cadence while sockets are down (default 30 s). */
  pollIntervalMs?: number;
  /** Backoff cap after consecutive polling errors (default 240 s). */
  maxPollBackoffMs?: number;
  /** Jitter source (injectable for deterministic tests). */
  random?: () => number;
  /** Listen to window online/offline (default true; tests may disable). */
  bindWindowEvents?: boolean;
}

const DISCONNECTED_STATES = new Set(['unavailable', 'failed', 'disconnected']);

export class RealtimeStore {
  state: RealtimeState = 'offline';
  organizationId: number | null = null;
  /** True when no Reverb env config exists — badge shows a permanent Polling mode. */
  readonly permanentPolling: boolean;

  private readonly orders: RealtimeOrdersLike;
  private readonly tables: RealtimeTablesLike | null;
  private readonly reverbConfig: AppEnv['reverb'];
  private readonly createClient: (config: NonNullable<AppEnv['reverb']>) => RealtimeClientLike;
  private readonly notifier: RealtimeNotifier | null;
  private readonly isDayOpen: () => boolean;
  private readonly disconnectGraceMs: number;
  private readonly pollIntervalMs: number;
  private readonly maxPollBackoffMs: number;
  private readonly random: () => number;

  private client: RealtimeClientLike | null = null;
  private channel: ChannelLike | null = null;
  private connectionCb: ConnectionCallback | null = null;
  private newOrderCb: EventCallback | null = null;
  private statusChangedCb: EventCallback | null = null;
  private graceTimer: ReturnType<typeof setTimeout> | null = null;
  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private consecutivePollErrors = 0;

  /** Once-only chime bookkeeping, kept per tab session and PER organization. */
  private readonly seenOrderIdsByOrg = new Map<number, Set<number>>();
  private readonly seededOrgs = new Set<number>();

  private readonly disposeActiveListener: () => void;
  private readonly disposeWindowListeners: () => void;

  constructor(options: RealtimeStoreOptions) {
    this.orders = options.orders;
    this.tables = options.tables ?? null;
    this.reverbConfig = options.reverb !== undefined ? options.reverb : env.reverb;
    this.permanentPolling = this.reverbConfig === null;
    this.createClient = options.createClient ?? ((config) => new EchoClient(config));
    this.notifier = options.notifier ?? null;
    this.isDayOpen = options.isDayOpen ?? (() => true);
    this.disconnectGraceMs = options.disconnectGraceMs ?? 10_000;
    // Without Reverb, polling is the primary notification transport rather
    // than a rare recovery path. Keep it short enough for an operator alert;
    // configured realtime installations retain the calmer 30-second fallback.
    this.pollIntervalMs = options.pollIntervalMs ?? (this.permanentPolling ? 5_000 : 30_000);
    this.maxPollBackoffMs = options.maxPollBackoffMs ?? 240_000;
    this.random = options.random ?? Math.random;

    makeAutoObservable<
      RealtimeStore,
      | 'orders'
      | 'tables'
      | 'reverbConfig'
      | 'createClient'
      | 'notifier'
      | 'isDayOpen'
      | 'disconnectGraceMs'
      | 'pollIntervalMs'
      | 'maxPollBackoffMs'
      | 'random'
      | 'client'
      | 'channel'
      | 'connectionCb'
      | 'newOrderCb'
      | 'statusChangedCb'
      | 'graceTimer'
      | 'pollTimer'
      | 'consecutivePollErrors'
      | 'seenOrderIdsByOrg'
      | 'seededOrgs'
      | 'disposeActiveListener'
      | 'disposeWindowListeners'
    >(
      this,
      {
        orders: false,
        tables: false,
        reverbConfig: false,
        createClient: false,
        notifier: false,
        isDayOpen: false,
        disconnectGraceMs: false,
        pollIntervalMs: false,
        maxPollBackoffMs: false,
        random: false,
        client: false,
        channel: false,
        connectionCb: false,
        newOrderCb: false,
        statusChangedCb: false,
        graceTimer: false,
        pollTimer: false,
        consecutivePollErrors: false,
        seenOrderIdsByOrg: false,
        seededOrgs: false,
        disposeActiveListener: false,
        disposeWindowListeners: false,
      },
      { autoBind: true },
    );

    this.disposeActiveListener = this.orders.onActiveReconciled((list) => this.scanForChime(list));

    if (options.bindWindowEvents !== false && typeof window !== 'undefined') {
      const onOffline = () => this.handleBrowserOffline();
      const onOnline = () => this.handleBrowserOnline();
      window.addEventListener('offline', onOffline);
      window.addEventListener('online', onOnline);
      this.disposeWindowListeners = () => {
        window.removeEventListener('offline', onOffline);
        window.removeEventListener('online', onOnline);
      };
    } else {
      this.disposeWindowListeners = () => {};
    }
  }

  // ── public lifecycle ─────────────────────────────────────────────────────

  /**
   * Connect after auth + organization selection. Calling with a different
   * organization tears the previous subscription down first (06-realtime §4).
   */
  connect(organizationId: number): void {
    this.teardownTransport();
    this.organizationId = organizationId;

    if (!this.reverbConfig) {
      // No key/host configured — permanent polling with a clear badge state.
      this.state = 'polling';
      this.startPolling({ immediate: true });
      return;
    }

    this.state = 'connecting';
    const client = this.createClient(this.reverbConfig);
    this.client = client;

    this.connectionCb = (payload) => this.handleConnectionChange(payload);
    client.connection.bind('state_change', this.connectionCb);

    const channel = client.subscribe(ordersChannel(organizationId));
    this.channel = channel;
    this.newOrderCb = (payload) => this.handleNewOrder(payload);
    this.statusChangedCb = (payload) => this.handleOrderStatusChanged(payload);
    channel.listen(NEW_ORDER_EVENT, this.newOrderCb);
    channel.listen(ORDER_STATUS_EVENT, this.statusChangedCb);

    // The fake (and an already-open pusher) can be connected synchronously.
    if (client.connection.state === 'connected') this.handleConnected();
  }

  /** Full teardown (logout / before org switch). Seen-chime ids are kept for the tab. */
  disconnect(): void {
    this.teardownTransport();
    this.organizationId = null;
    this.state = 'offline';
  }

  /** Complete disposal (tests / app teardown). */
  dispose(): void {
    this.disconnect();
    this.disposeActiveListener();
    this.disposeWindowListeners();
  }

  /** Unbind + unsubscribe + close the socket and clear all timers. */
  private teardownTransport(): void {
    this.clearGraceTimer();
    this.stopPolling();
    this.consecutivePollErrors = 0;
    if (this.channel) {
      if (this.newOrderCb) this.channel.stopListening(NEW_ORDER_EVENT, this.newOrderCb);
      if (this.statusChangedCb)
        this.channel.stopListening(ORDER_STATUS_EVENT, this.statusChangedCb);
    }
    if (this.client) {
      if (this.channel) this.client.unsubscribe(this.channel.name);
      if (this.connectionCb) this.client.connection.unbind('state_change', this.connectionCb);
      this.client.disconnect();
    }
    this.channel = null;
    this.client = null;
    this.connectionCb = null;
    this.newOrderCb = null;
    this.statusChangedCb = null;
  }

  // ── event handlers (payloads parsed minimally, NEVER logged) ─────────────

  private handleNewOrder(payload: unknown): void {
    const hint = extractNewOrderHint(payload);
    if (hint?.source === 'web') this.announceRealtimeHint(hint);
    this.orders.notifyExternalChange({ kind: 'new', orderId: hint?.id ?? extractOrderId(payload) });
    // A new order may be a tab just opened on another device.
    this.tables?.notifyExternalChange();
  }

  private handleOrderStatusChanged(payload: unknown): void {
    this.orders.notifyExternalChange({ kind: 'status', orderId: extractOrderId(payload) });
    // A status change may be a tab being settled, which frees its table.
    this.tables?.notifyExternalChange();
  }

  private handleConnectionChange(payload: unknown): void {
    const current =
      payload && typeof payload === 'object' && 'current' in payload
        ? String((payload as { current: unknown }).current)
        : null;
    if (current === 'connected') {
      this.handleConnected();
    } else if (current !== null && DISCONNECTED_STATES.has(current)) {
      this.handleDisconnected();
    } else if (current === 'connecting' && this.state === 'live') {
      // pusher retry loop after a drop
      this.handleDisconnected();
    }
  }

  private handleConnected(): void {
    if (this.state === 'live') return;
    this.clearGraceTimer();
    this.stopPolling();
    this.state = 'live';
    this.consecutivePollErrors = 0;
    // ONE immediate full refresh before relying on events again. The floor is
    // refreshed alongside the board: events missed while the socket was down
    // could have opened or settled any tab.
    void this.orders.reconcileActive();
    void this.tables?.reconcileFloor();
  }

  private handleDisconnected(): void {
    if (this.state === 'reconnecting' || this.state === 'polling' || this.state === 'offline') {
      return;
    }
    this.state = 'reconnecting';
    this.clearGraceTimer();
    this.graceTimer = setTimeout(() => {
      this.graceTimer = null;
      if (this.state === 'reconnecting') {
        this.state = 'polling';
        this.startPolling({ immediate: true });
      }
    }, this.disconnectGraceMs);
  }

  // ── polling fallback ─────────────────────────────────────────────────────

  private startPolling(options: { immediate?: boolean } = {}): void {
    this.stopPolling();
    this.schedulePoll(options.immediate ? 0 : this.pollIntervalMs);
  }

  private schedulePoll(delayMs: number): void {
    this.pollTimer = setTimeout(() => {
      this.pollTimer = null;
      void this.runPoll();
    }, delayMs);
  }

  private async runPoll(): Promise<void> {
    if (this.state !== 'polling') return;
    // The floor polls alongside the board. Backoff keys off the board only:
    // reconcileFloor() returns false when no branch is selected, which is not
    // a transport failure and must not slow the poll down.
    const [ok] = await Promise.all([
      this.orders.reconcileActive(),
      this.tables?.reconcileFloor() ?? Promise.resolve(true),
    ]);
    // Reconnected (or torn down) while the poll was in flight — do not reschedule.
    if (this.state !== 'polling') return;
    if (ok) {
      this.consecutivePollErrors = 0;
      this.schedulePoll(this.pollIntervalMs);
    } else {
      this.consecutivePollErrors += 1;
      this.schedulePoll(this.backoffDelay());
    }
  }

  /** interval × 2^errors, capped, with ±20 % jitter. */
  private backoffDelay(): number {
    const exponential = this.pollIntervalMs * 2 ** this.consecutivePollErrors;
    const capped = Math.min(exponential, this.maxPollBackoffMs);
    const jitter = 0.8 + 0.4 * this.random();
    return Math.round(capped * jitter);
  }

  private stopPolling(): void {
    if (this.pollTimer !== null) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private clearGraceTimer(): void {
    if (this.graceTimer !== null) {
      clearTimeout(this.graceTimer);
      this.graceTimer = null;
    }
  }

  // ── browser connectivity (online-only console; polling ≠ offline mode) ──

  private handleBrowserOffline(): void {
    if (this.organizationId === null) return;
    this.clearGraceTimer();
    this.stopPolling();
    this.state = 'offline';
  }

  private handleBrowserOnline(): void {
    if (this.organizationId === null || this.state !== 'offline') return;
    if (this.permanentPolling || !this.client) {
      this.state = 'polling';
      this.startPolling({ immediate: true });
      return;
    }
    if (this.client.connection.state === 'connected') {
      this.state = 'connecting'; // handleConnected() no-ops on 'live' only
      this.handleConnected();
      return;
    }
    // Give the socket its reconnect window; fall back to polling after grace.
    this.state = 'connecting';
    this.handleDisconnected();
  }

  // ── chime (once-only per order id per tab session) ───────────────────────

  private announceRealtimeHint(hint: NewOrderHint): void {
    const orgId = this.organizationId;
    if (orgId === null || !this.isDayOpen()) return;

    let seen = this.seenOrderIdsByOrg.get(orgId);
    if (!seen) {
      seen = new Set<number>();
      this.seenOrderIdsByOrg.set(orgId, seen);
    }
    if (seen.has(hint.id)) return;

    // Mark before any asynchronous audio/notification work. The HTTP refresh
    // caused by this same event will enrich the board without announcing the
    // order a second time.
    seen.add(hint.id);
    const notifier = this.notifier;
    if (!notifier) return;
    notifier.onNewOrderHint?.(hint);
    void notifier.playChime().catch(() => undefined);
  }

  private scanForChime(orders: readonly Order[]): void {
    const orgId = this.organizationId;
    if (orgId === null) return;
    let seen = this.seenOrderIdsByOrg.get(orgId);
    if (!seen) {
      seen = new Set<number>();
      this.seenOrderIdsByOrg.set(orgId, seen);
    }

    if (!this.seededOrgs.has(orgId)) {
      // First successful board fetch for this org in this tab: seed silently —
      // pre-existing orders are presented on the board, not announced.
      for (const order of orders) seen.add(order.id);
      this.seededOrgs.add(orgId);
      return;
    }

    const unseenWeb: Order[] = [];
    for (const order of orders) {
      if (order.source === 'web' && !seen.has(order.id)) unseenWeb.push(order);
      else seen.add(order.id);
    }
    if (unseenWeb.length === 0) return;

    if (!this.isDayOpen()) {
      // Day not open: leave them unseen so they announce once the day opens.
      return;
    }

    // Mark BEFORE playing so overlapping reconciles can never double-chime.
    for (const order of unseenWeb) seen.add(order.id);
    const notifier = this.notifier;
    if (!notifier) return;
    // Sound and notification are separate promises to the operator. A working
    // speaker must not suppress the on-screen alert, and a muted/blocked
    // speaker must not produce the same toast twice.
    notifier.onNewOrders?.(unseenWeb);
    void notifier
      .playChime()
      .then((played) => {
        if (!played && !notifier.onNewOrders) notifier.onChimeBlocked(unseenWeb);
      })
      .catch(() => {
        if (!notifier.onNewOrders) notifier.onChimeBlocked(unseenWeb);
      });
  }
}
